<script lang="ts">
  import ChatMessage from './ChatMessage.svelte';
  import type { Message } from '../types';
  import { appState } from '../state.svelte';
  import { t } from '../i18n/index';

  let {
    visitorId = '',
    onSend,
    onUpload,
  }: {
    visitorId?: string;
    onSend: (text: string) => void;
    onUpload?: (files: File[]) => void;
  } = $props();

  let draft = $state('');
  let sending = $state(false);
  let threadEl: HTMLDivElement | undefined = $state();
  let fileInput: HTMLInputElement | undefined = $state();
  let dragDepth = $state(0); // count of overlapping dragenter events
  const uploadsVisible = $derived(
    appState.uploads.filter((u) => u.status !== 'done')
  );
  const uploadEnabled = $derived(
    !!(appState.widgetConfig?.allow_upload) && typeof onUpload === 'function'
  );

  function pickFiles(list: FileList | File[] | null | undefined): File[] {
    if (!list) return [];
    return Array.from(list);
  }

  function handleFilePick(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const files = pickFiles(input.files);
    if (files.length && onUpload) onUpload(files);
    // Reset so picking the same file twice in a row still fires onChange.
    input.value = '';
  }

  function handleDragEnter(event: DragEvent) {
    if (!uploadEnabled) return;
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    dragDepth++;
  }

  function handleDragOver(event: DragEvent) {
    if (!uploadEnabled) return;
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  function handleDragLeave(event: DragEvent) {
    if (!uploadEnabled) return;
    event.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
  }

  function handleDrop(event: DragEvent) {
    if (!uploadEnabled) return;
    event.preventDefault();
    dragDepth = 0;
    const files = pickFiles(event.dataTransfer?.files);
    if (files.length && onUpload) onUpload(files);
  }

  // Smooth auto-scroll on any thread-length change (new incoming message,
  // self-send echo, clear, initial load) OR when the AI thinking indicator
  // appears/disappears. Reading primitive dep triggers ensures Svelte only
  // re-runs the effect on meaningful changes.
  $effect(() => {
    const _count = appState.messages.length;
    const _thinking = appState.aiThinking;
    void _count;
    void _thinking;
    if (!threadEl) return;
    // Double-rAF: wait one frame for Svelte to flush the new message node,
    // then wait a second frame so the layout pass has extended scrollHeight
    // before we read it. Without this, smooth scrolling can stop short.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!threadEl) return;
        threadEl.scrollTo({
          top: threadEl.scrollHeight,
          behavior: 'smooth',
        });
      });
    });
  });

  const greetingMessage = $derived.by(() => {
    const cfg = appState.widgetConfig;
    if (!cfg || !cfg.greeting_message) return null;
    const chat = appState.chat;
    const msg: Message = {
      _id: '__greeting__',
      chat_id: chat ? chat._id : '',
      source: 'Bot',
      type: 'message',
      message: cfg.greeting_message,
      created_by: 'greeting',
      created_at: '',
    };
    return msg;
  });

  async function handleSubmit(event: SubmitEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    sending = true;
    try {
      onSend(text);
      draft = '';
    } finally {
      sending = false;
    }
  }

  function handleKey(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const form = (event.currentTarget as HTMLTextAreaElement).form;
      form?.requestSubmit();
    }
  }
</script>

<div
  class="tashimoto-support-flex tashimoto-support-flex-col tashimoto-support-flex-1 tashimoto-support-min-h-0 tashimoto-support-bg-gray-50 tashimoto-support-relative"
  data-testid="support-chat"
  ondragenter={handleDragEnter}
  ondragover={handleDragOver}
  ondragleave={handleDragLeave}
  ondrop={handleDrop}
>
  {#if uploadEnabled && dragDepth > 0}
    <!-- Drag overlay — covers the whole chat area so anywhere the visitor
         drops a file gets captured. pointer-events-none on children means
         the outer div still receives dragleave / drop events. -->
    <div
      class="tashimoto-support-absolute tashimoto-support-inset-0 tashimoto-support-z-10 tashimoto-support-bg-blue-500/10 tashimoto-support-border-2 tashimoto-support-border-dashed tashimoto-support-border-blue-400 tashimoto-support-flex tashimoto-support-items-center tashimoto-support-justify-center tashimoto-support-pointer-events-none"
      aria-hidden="true"
    >
      <div class="tashimoto-support-text-sm tashimoto-support-font-medium tashimoto-support-text-blue-700 tashimoto-support-bg-white tashimoto-support-px-3 tashimoto-support-py-1.5 tashimoto-support-rounded-full tashimoto-support-shadow">
        {t('chat.dropToUpload')}
      </div>
    </div>
  {/if}
  <div
    bind:this={threadEl}
    class="tashimoto-support-flex-1 tashimoto-support-overflow-y-auto tashimoto-support-p-3 tashimoto-support-flex tashimoto-support-flex-col tashimoto-support-scroll-smooth"
    data-testid="support-chat-thread"
  >
    {#if greetingMessage}
      <ChatMessage
        message={greetingMessage}
        visitorId={visitorId}
        visitorName={appState.visitor.name}
        greeting
      />
    {/if}

    {#each appState.messages.filter(m => {
      // 'human-interaction-stop' is an agent-side signal (AI resumed). Hidden
      // from the visitor; portal still shows it as a timeline marker.
      if (m.type === 'human-interaction-stop') return false;
      // Visitor-initiated clear marker — the server sets created_by to the
      // visitor's id on this System message. The visitor just cleared their
      // own view, so echoing "Visitor cleared their view" back to them would
      // be noisy. Agents in the portal still see it (they render all source='System').
      if (m.type === 'system' && m.source === 'System' && visitorId && m.created_by === visitorId) return false;
      return true;
    }) as msg (msg._id)}
      <ChatMessage
        message={msg}
        visitorId={visitorId}
        visitorName={appState.visitor.name}
      />
    {/each}

    {#if appState.aiThinking}
      <div
        class="tashimoto-support-flex tashimoto-support-w-full tashimoto-support-my-1 tashimoto-support-justify-start"
        data-testid="ai-thinking"
        aria-live="polite"
      >
        <div class="tashimoto-support-flex tashimoto-support-items-end tashimoto-support-gap-2">
          <div
            class="tashimoto-support-w-[28px] tashimoto-support-h-[28px] tashimoto-support-rounded-full tashimoto-support-bg-purple-100 tashimoto-support-flex tashimoto-support-items-center tashimoto-support-justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tashimoto-support-text-purple-600" aria-hidden="true">
              <rect x="3" y="11" width="18" height="10" rx="2"></rect>
              <circle cx="12" cy="5" r="2"></circle>
              <path d="M12 7v4"></path>
              <line x1="8" y1="16" x2="8" y2="16"></line>
              <line x1="16" y1="16" x2="16" y2="16"></line>
            </svg>
          </div>
          <div
            class="tashimoto-support-mx-1 tashimoto-support-p-3 tashimoto-support-rounded-lg tashimoto-support-bg-purple-50 tashimoto-support-border tashimoto-support-border-purple-200 tashimoto-support-rounded-bl-none"
          >
            <span class="tashimoto-support-text-xs tashimoto-support-font-semibold tashimoto-support-text-purple-600 tashimoto-support-mr-2">
              {t('chat.aiAgent')}
            </span>
            <span class="tashimoto-support-inline-flex tashimoto-support-gap-1 tashimoto-support-items-center tashimoto-support-align-middle">
              <span class="tashimoto-support-typing-dot"></span>
              <span class="tashimoto-support-typing-dot" style="animation-delay: 150ms"></span>
              <span class="tashimoto-support-typing-dot" style="animation-delay: 300ms"></span>
            </span>
          </div>
        </div>
      </div>
    {/if}
  </div>

  {#if uploadsVisible.length > 0}
    <div
      class="tashimoto-support-border-t tashimoto-support-bg-white tashimoto-support-px-3 tashimoto-support-py-2 tashimoto-support-space-y-1"
      data-testid="support-chat-uploads"
    >
      {#each uploadsVisible as up (up.id)}
        <div class="tashimoto-support-flex tashimoto-support-items-center tashimoto-support-gap-2 tashimoto-support-text-xs">
          <span class="tashimoto-support-flex-1 tashimoto-support-truncate">{up.name}</span>
          {#if up.status === 'error'}
            <span class="tashimoto-support-text-red-600">
              {t('upload.error.' + (up.error || 'upload_failed'))}
            </span>
          {:else}
            <span class="tashimoto-support-w-20 tashimoto-support-h-1.5 tashimoto-support-bg-gray-200 tashimoto-support-rounded-full tashimoto-support-overflow-hidden">
              <span
                class="tashimoto-support-block tashimoto-support-h-full tashimoto-support-bg-blue-500"
                style="width: {Math.round(up.progress * 100)}%"
              ></span>
            </span>
            <span class="tashimoto-support-text-gray-500 tashimoto-support-tabular-nums">
              {Math.round(up.progress * 100)}%
            </span>
          {/if}
        </div>
      {/each}
    </div>
  {/if}

  <form
    class="tashimoto-support-border-t tashimoto-support-bg-white tashimoto-support-p-3 tashimoto-support-flex tashimoto-support-gap-2 tashimoto-support-items-end"
    onsubmit={handleSubmit}
    data-testid="support-chat-composer"
  >
    {#if uploadEnabled}
      <input
        bind:this={fileInput}
        type="file"
        multiple
        class="tashimoto-support-hidden"
        onchange={handleFilePick}
        data-testid="support-chat-file-input"
      />
      <button
        type="button"
        class="tashimoto-support-p-2 tashimoto-support-text-gray-500 hover:tashimoto-support-text-blue-600 tashimoto-support-rounded-lg hover:tashimoto-support-bg-gray-100 tashimoto-support-flex-shrink-0"
        onclick={() => fileInput?.click()}
        aria-label={t('chat.attachFile')}
        title={t('chat.attachFile')}
        data-testid="support-chat-attach"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
        </svg>
      </button>
    {/if}
    <textarea
      rows="2"
      bind:value={draft}
      placeholder={t('chat.typeMessage')}
      onkeydown={handleKey}
      disabled={sending}
      class="tashimoto-support-flex-1 tashimoto-support-border tashimoto-support-rounded-lg tashimoto-support-px-3 tashimoto-support-py-2 tashimoto-support-text-sm tashimoto-support-resize-none focus:tashimoto-support-outline-none focus:tashimoto-support-ring-2 focus:tashimoto-support-ring-blue-500"
      data-testid="support-chat-input"
    ></textarea>
    <button
      type="submit"
      disabled={sending || !draft.trim()}
      class="tashimoto-support-px-4 tashimoto-support-py-2 tashimoto-support-bg-blue-500 hover:tashimoto-support-bg-blue-600 disabled:tashimoto-support-bg-blue-300 tashimoto-support-text-white tashimoto-support-rounded-lg tashimoto-support-text-sm tashimoto-support-font-medium tashimoto-support-flex-shrink-0"
      data-testid="support-chat-send"
    >
      {t('common.send')}
    </button>
  </form>
</div>

<style>
  .tashimoto-support-typing-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background-color: rgb(147 51 234 / 0.7); /* purple-600 @ 70% */
    animation: tashimoto-support-typing-bounce 1.2s ease-in-out infinite;
  }
  @keyframes tashimoto-support-typing-bounce {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
    30% { transform: translateY(-3px); opacity: 1; }
  }
</style>
